// `…/candidate-messages` — the email composer, in a side view.
//
// The recipients come from the same place the add-to-job panel's do: the profile state seeds
// the checkbox container with the candidate on screen, and the review screen's selection fills
// it there. See `AddToJobRoute` for the shape.
import { useCallback, useEffect, useState, type ReactElement } from 'react'
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import {
  getMessageSignature,
  saveMessageSignature,
  getRemainingDailyMessages,
  outwardWriteOutcome,
  sendCandidateMessages,
  sendClientContactMessages,
} from '@woo-ext/api'
import { ExtensionViewWidth } from '@woo-ext/protocol'
import { MessageComposer, messageSentOutcome, type SignatureLoadState } from '@woo-ext/ui'
import { trackMessageSendButton } from './actionTracking.ts'

export interface CandidateMessagesRouteProps {
  closePath?: string
  /** Picks the host element type and the noun; see `MessageComposer`. */
  entity?: 'candidates' | 'clientContacts'
}

interface SideViewContext {
  checked?: ReadonlyArray<{ id: number; name?: string | null }>
}

export function CandidateMessagesRoute({ closePath, entity = 'candidates' }: CandidateMessagesRouteProps): ReactElement {
  const context = useOutletContext<SideViewContext | null>()
  const recipients = context?.checked ?? []
  const [params] = useSearchParams()
  const navigate = useNavigate()

  const [signature, setSignature] = useState('')
  const [signatureLoadState, setSignatureLoadState] = useState<SignatureLoadState>('loading')
  const [isLoaded, setLoaded] = useState(false)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  // `remainingDailyEntities`. ⚠️ UNDEFINED UNTIL IT IS FETCHED, as upstream's field is: the
  // composer treats that as 0 for `canSendMessage` (strictly greater than zero, so Send stays
  // inert while loading) and as "not reached" for the disabled-Send tooltip, which would
  // otherwise announce a daily limit nobody has hit.
  const [remainingDaily, setRemainingDaily] = useState<number | undefined>(undefined)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const rawAlertId = params.get('alertId')
  const parsedAlertId = rawAlertId === null ? Number.NaN : Number.parseInt(rawAlertId, 10)
  const alertId = Number.isNaN(parsedAlertId) ? undefined : parsedAlertId

  /**
   * `loadSignature()` (upstream woo@10e2fe0302). `isLoaded` flips either way, so the screen
   * appears whatever happened — but ⚠️ A FAILED READ IS `'failed'`, NOT AN EMPTY SIGNATURE. It
   * used to be swallowed into "no signature", which left an empty editor that could be opened
   * and saved over the stored one (HubSpot 49214431098). Now the editor stays locked and offers
   * RETRY. A user who has never set one is not a failure: the server answers 200 with a null.
   */
  const loadSignature = useCallback((isLive: () => boolean): void => {
    setSignatureLoadState('loading')
    getMessageSignature().then(
      (loaded) => {
        if (!isLive()) return
        setSignature(loaded?.signature ?? '')
        setSignatureLoadState('loaded')
        setLoaded(true)
      },
      () => {
        if (!isLive()) return
        setSignatureLoadState('failed')
        setLoaded(true)
      },
    )
  }, [])

  useEffect(() => {
    let live = true
    loadSignature(() => live)
    return () => {
      live = false
    }
  }, [loadSignature])

  useEffect(() => {
    let live = true
    // `initRemainingDailyCandidates()`. Its failure leaves the cap at 0 and the button dead,
    // which is the safe direction: a cap this screen could not read is not a cap of zero, but
    // mailing on the strength of a failed read is the one mistake that cannot be undone.
    getRemainingDailyMessages(entity).then(
      (remaining) => {
        if (live) setRemainingDaily(typeof remaining === 'number' ? remaining : 0)
      },
      () => undefined,
    )
    return () => {
      live = false
    }
  }, [entity])

  /**
   * `close()` — `$state.go(this.closeState, this.closeParams)`.
   *
   * ⚠️ IT IS ALSO WHERE A SUCCESSFUL SEND LANDS, which is upstream's shape too: `onMessageSent`
   * navigates back to the screen the composer was opened over
   * (`extension.state.tabs.config.ng1.ts:1135`). Declared before the composer so both the X
   * and the send can reach it.
   */
  const close = (): void => {
    if (closePath === undefined) return
    const candidateId = params.get('candidateId')
    const search = new URLSearchParams()
    if (alertId !== undefined) search.set('alertId', String(alertId))
    if (candidateId !== null) search.set('candidateId', candidateId)
    void navigate(search.size === 0 ? closePath : `${closePath}?${search.toString()}`)
  }

  return (
    <MessageComposer
      isLoaded={isLoaded}
      entity={entity}
      recipients={recipients}
      subject={subject}
      body={body}
      signature={signature}
      signatureLoadState={signatureLoadState}
      // `retryLoadSignature()`. Not tied to the mount's liveness flag — the click is the
      // composer's own, and the composer is mounted.
      onRetryLoadSignature={() => {
        loadSignature(() => true)
      }}
      // `saveSignature(clear)` in `extension-alert-entity-messages-controller`. A clear sends
      // `null`, never the blank markup in the editor — the server needs `clear` for both.
      onSaveSignature={(clear) => saveMessageSignature({ signature: clear ? null : signature, clear })}
      {...(alertId === undefined ? {} : { alertId })}
      // `viaHunter` is `$state.includes(Hunter)`; none of the three routes this serves is under
      // it, so the To field lists its recipients rather than counting them.
      viaHunter={false}
      flexBasis={ExtensionViewWidth.Extended}
      remainingDaily={remainingDaily}
      // ⚠️ `recipients.length`, AND UPSTREAM'S OWN VALUE IS NARROWER. There it is
      // `validAndAvailableEntitiesToSend` — the checked entities minus the ones carrying a
      // `blacklistReason`, which the checkbox container knows and this port's selection does
      // not carry. Passing the full count hands the filtering to the endpoint named for it
      // (`filterAndSendMessageToCandidatesByIds`), which answers with `blacklistedIds` — so
      // nobody is mailed who should not be, and the screen reports the difference afterwards
      // rather than before. Passing 0 instead would be an inert button; inventing a filter
      // here would be a second, divergent copy of a rule the server already owns.
      sendableCount={recipients.length}
      sending={sending}
      {...(error === undefined ? {} : { error })}
      /**
       * ⚠️ THIS PUTS MAIL IN A CANDIDATE'S INBOX AND IT CANNOT BE WITHDRAWN. It fires only
       * from the Send button, only when `canSendMessage` holds — the composer re-checks the
       * five conjuncts in the handler, not just in the styling — and `sending` stops a second
       * press from mailing twice.
       *
       * ⚠️ `filter: {}` IS SENT EXPLICITLY AND IT IS NOT A PLACEHOLDER. The endpoint filters
       * the id list — blacklists, missing addresses, already-messaged — and omitting the key
       * means no filter is applied rather than "send to all": it would mail the people the
       * UI had already excluded. Upstream collects the object from a modal this port has not
       * got (`extension-alert-candidates-message-filter-modal.tpl.html`); an empty object is
       * what that modal sends when nothing is ticked, which is the honest stand-in.
       *
       * ⚠️ AND A NETWORK FAILURE CLOSES THE COMPOSER RATHER THAN REPORTING ONE — see
       * `outwardWriteOutcome`. The send is enqueued and deduped server-side, so a request
       * that never got an answer may well have landed; saying it failed invites a second
       * press, and for mail that means two copies in a candidate's inbox.
       */
      // `onSendButtonClick` records the press before anything else — `checkedEntities` and
      // `validAndAvailableEntitiesToSend`, which here are both the recipients (see
      // `sendableCount`: this selection carries no blacklist reason to subtract).
      onSendButtonClick={() => {
        trackMessageSendButton(entity, recipients.length, recipients.length)
      }}
      onSend={() => {
        if (sending) return
        setSending(true)
        setError(undefined)
        const ids = recipients.map((recipient) => recipient.id)
        const payload = {
          // `this.subject.trim()` — upstream trims because the field sets `ng-trim="false"`.
          emailSubject: subject.trim(),
          emailBody: body,
          emailSignature: signature,
          ...(alertId === undefined ? {} : { alertId }),
          filter: {},
        }
        const sent =
          entity === 'clientContacts'
            ? sendClientContactMessages({ ...payload, clientContactIds: ids })
            : sendCandidateMessages({ ...payload, candidateIds: ids })
        void sent.then(
          (response) => {
            setSending(false)
            // ⚠️ A 200 IS NOT "EVERYONE WAS MAILED" — see `messageSentOutcome`, which carries
            // upstream's own two sentences. Nothing filtered: close, as `onMessageSent` does.
            // Someone filtered: stay open and say so, because closing would take the only
            // report of it with it.
            const outcome = messageSentOutcome(response, entity === 'clientContacts' ? 'contact' : 'candidate')
            if (outcome.close) close()
            else setError(outcome.message)
          },
          (failure: unknown) => {
            setSending(false)
            const outcome = outwardWriteOutcome(failure, 'Something went wrong sending your message, please try again.')
            // `silent` is a 403: the auth layer owns it and is about to redirect.
            if (outcome.report === 'error') setError(outcome.message)
            else close()
          },
        )
      }}
      onSubjectChange={setSubject}
      onBodyChange={setBody}
      onSignatureChange={setSignature}
      onClose={close}
    />
  )
}
